import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const FramePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { frames, config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.frame
  const { setSessionState } = useSessionStateStore()
  const [selectedFrame, setSelectedFrame] = useState<string | null>(frames[0]._id)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)
  const [remainingTime, setRemainingTime] = useState<string>('--')

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const countdown = useCountdown(frameTimeout)

  useEffect(() => {
    if (frames.length > 0) {
      setSelectedFrame(frames[0]._id)
    }
  }, [frames])

  const handleNext = (): void => {
    if (!selectedFrame) return

    const frame = frames.find((f) => f._id === selectedFrame)
    if (!frame) return

    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      },
      timeline: {
        activityLogs: [
          {
            page: 'frame',
            type: 'selection',
            action: 'complete',
            details: { frameId: selectedFrame },
            timestamp: new Date()
          }
        ]
      }
    })
    navigate(`/${projectId}/checkout`)
  }

  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      countdown.setOnComplete(() => {
        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'frame',
                type: 'selection',
                action: 'timeout',
                details: {
                  frameId: selectedFrame,
                  timeoutDuration: frameTimeout
                },
                timestamp: new Date()
              }
            ]
          }
        })
        handleNext()
      })
    }
  }, [selectedFrame, frameTimeout, setSessionState])

  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.start()
      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [frameTimeout])

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-frame.webp')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedBackground})` }}
      data-page="frame"
    >
      <div className="w-full h-full flex">
        {/* Frame Selection */}
        <div className="flex-1 flex flex-col w-full max-w-[70%]">
          {/* Category Tabs - Only show if frameCategories exist and are not empty */}
          {config?.settings?.frameCategories && config.settings.frameCategories.length > 0 && (
            <div className="w-full hidden">
              <div className="overflow-x-auto w-full flex justify-left gap-4 px-12 py-4">
                {config?.settings?.frameCategories.map((category) => (
                  <button
                    type="button"
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`px-8 py-3 rounded-lg text-xl whitespace-nowrap transition-all
                      ${selectedCategory === category.name ? 'bg-carnabyTan text-foundationWhite' : 'text-nightBrown'}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Frames Grid */}
          <div
            className={`flex-1 overflow-y-auto px-16 mt-40 ${config?.settings?.frameCategories?.length ? 'py-12' : 'py-8'}`}
          >
            <div
              className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-12 items-end`}
              role="listbox"
              title={t('frame.framesListbox', 'Frames')}
            >
              {frames.map((frame) => (
                <button
                  role="option"
                  aria-label={frame.name}
                  type="button"
                  key={frame._id}
                  onClick={() => setSelectedFrame(frame._id)}
                  className={`cursor-pointer transition-all flex flex-col p-8 items-center
                    ${selectedFrame === frame._id ? 'scale-105' : ''}`}
                >
                  <img
                    crossOrigin="anonymous"
                    src={frame.thumbnail}
                    alt={frame.name}
                    className={`w-full object-contain rounded-lg ${
                      selectedFrame === frame._id
                        ? 'border-4 border-carnabyTan'
                        : 'border border-gray-100'
                    }`}
                  />
                  <p className="mt-2 text-carnabyTan font-deco-samu text-3xl">{frame.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Frame Preview */}
        <div
          className={`flex flex-col items-center justify-center gap-12 w-full ${isPotrait ? 'p-12 max-w-[55%]' : 'p-8 max-w-[35%]'}`}
        >
          <div className="w-[426px] px-4">
            <img
              src={frames.find((f) => f._id === selectedFrame)?.thumbnail || ''}
              alt={t('frame.previewAlt', 'Preview')}
              crossOrigin="anonymous"
              className="w-full object-contain"
            />
          </div>

          <div className="flex flex-col items-center gap-4">
            {(config?.settings?.frameTimeout || 0) > 0 && (
              <span className="text-3xl text-foundationWhite">{remainingTime}</span>
            )}
            <button
              onClick={handleNext}
              className="rounded-lg bg-carnabyTan text-foundationWhite font-body-samu tracking-widest px-24 hover:brightness-110 active:brightness-110 py-6 text-3xl"
            >
              Lanjut
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FramePage
